import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Autocomplete,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  QuestionMark as QuestionIcon,
  Description as LeafIcon,
} from '@mui/icons-material';
import jsMind from 'jsmind';
import 'jsmind/style/jsmind.css';
import { toast } from 'react-toastify';
import { Setup, SetupNode } from '../types';
import setupService from '../services/setup.service';
import sessionService, { Session } from '../services/session.service';

interface SetupMindMapProps {
  setup: Setup;
  onSetupChange: () => void;
}

const SetupMindMap: React.FC<SetupMindMapProps> = ({ setup, onSetupChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const jmRef = useRef<any>(null);
  const [selectedNode, setSelectedNode] = useState<SetupNode | null>(null);

  // Dialogs
  const [addNodeDialogOpen, setAddNodeDialogOpen] = useState(false);
  const [editNodeDialogOpen, setEditNodeDialogOpen] = useState(false);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);

  // Form state
  const [newNodeText, setNewNodeText] = useState('');
  const [newNodeType, setNewNodeType] = useState<'question' | 'leaf'>('question');
  const [editNodeText, setEditNodeText] = useState('');
  const [editNodeType, setEditNodeType] = useState<'question' | 'leaf'>('question');

  // Sessions state
  const [availableSessions, setAvailableSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Find node by ID in tree
  const findNodeById = (nodes: SetupNode[], id: number): SetupNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = findNodeById(node.children || [], id);
      if (found) return found;
    }
    return null;
  };

  // Convert setup nodes to jsMind format
  const convertToJsMindData = (nodes: SetupNode[]): any => {
    const rootNode = nodes[0];
    if (!rootNode) {
      return {
        meta: { name: 'setup', author: '', version: '1.0' },
        format: 'node_tree',
        data: { id: 'root', topic: 'Aucun nœud', children: [] }
      };
    }

    const convertNode = (node: SetupNode): any => ({
      id: `node_${node.id}`,
      topic: node.text,
      direction: 'right',
      expanded: true,
      'background-color': node.node_type === 'question' ? '#fff3e0' : '#e8f5e9',
      children: (node.children || []).map(convertNode)
    });

    return {
      meta: { name: 'setup', author: '', version: '1.0' },
      format: 'node_tree',
      data: convertNode(rootNode)
    };
  };

  // Initialize jsMind
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !setup.nodes || setup.nodes.length === 0) return;

    const options = {
      container: container,
      editable: false,
      theme: 'primary',
      mode: 'full' as const,
      support_html: false,
      view: {
        engine: 'canvas' as const,
        hmargin: 100,
        vmargin: 50,
        line_width: 2,
        line_color: '#555'
      },
      layout: {
        hspace: 30,
        vspace: 20,
        pspace: 13
      }
    };

    // Destroy previous instance
    if (jmRef.current) {
      jmRef.current = null;
    }

    const jm = new jsMind(options);
    jmRef.current = jm;

    const mindData = convertToJsMindData(setup.nodes);
    jm.show(mindData);

    // Add click handler
    const handleClick = (e: Event) => {
      const selectedMindNode = jm.get_selected_node();
      if (selectedMindNode && selectedMindNode.id) {
        const nodeIdStr = String(selectedMindNode.id);
        const id = parseInt(nodeIdStr.replace('node_', ''));
        const node = findNodeById(setup.nodes, id);
        setSelectedNode(node);
      }
    };

    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('click', handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup.nodes]);

  // Load available sessions
  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const sessions = await sessionService.getAll();
      setAvailableSessions(sessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  // Add child node
  const handleAddNode = async () => {
    if (!selectedNode || !newNodeText.trim()) return;

    try {
      await setupService.createNode(setup.id, {
        parent: selectedNode.id,
        node_type: newNodeType,
        text: newNodeText.trim(),
        order: (selectedNode.children?.length || 0)
      });
      setNewNodeText('');
      setNewNodeType('question');
      setAddNodeDialogOpen(false);
      onSetupChange();
      toast.success('Nœud ajouté');
    } catch (error) {
      console.error('Error adding node:', error);
      toast.error('Erreur lors de l\'ajout du nœud');
    }
  };

  // Update node
  const handleUpdateNode = async () => {
    if (!selectedNode || !editNodeText.trim()) return;

    try {
      await setupService.updateNode(setup.id, selectedNode.id, {
        text: editNodeText.trim(),
        node_type: editNodeType
      });
      setEditNodeDialogOpen(false);
      onSetupChange();
      toast.success('Nœud modifié');
    } catch (error) {
      console.error('Error updating node:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  // Delete node
  const handleDeleteNode = async () => {
    if (!selectedNode) return;

    // Check if it's the root node
    if (!selectedNode.parent) {
      toast.error('Impossible de supprimer le nœud racine');
      return;
    }

    if (!window.confirm(`Supprimer "${selectedNode.text}" et tous ses enfants ?`)) {
      return;
    }

    try {
      await setupService.deleteNode(setup.id, selectedNode.id);
      setSelectedNode(null);
      onSetupChange();
      toast.success('Nœud supprimé');
    } catch (error) {
      console.error('Error deleting node:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Add session to leaf
  const handleAddSession = async (session: Session) => {
    if (!selectedNode) return;

    try {
      await setupService.addSessionToNode(setup.id, selectedNode.id, session.id);
      onSetupChange();
      toast.success('Séance ajoutée');
    } catch (error) {
      console.error('Error adding session:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Remove session from leaf
  const handleRemoveSession = async (sessionId: number) => {
    if (!selectedNode) return;

    try {
      await setupService.removeSessionFromNode(setup.id, selectedNode.id, sessionId);
      onSetupChange();
      toast.success('Séance retirée');
    } catch (error) {
      console.error('Error removing session:', error);
      toast.error('Erreur lors du retrait');
    }
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            if (!selectedNode) {
              toast.info('Sélectionnez un nœud parent');
              return;
            }
            setNewNodeText('');
            setNewNodeType('question');
            setAddNodeDialogOpen(true);
          }}
          disabled={!selectedNode}
        >
          Ajouter enfant
        </Button>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => {
            if (selectedNode) {
              setEditNodeText(selectedNode.text);
              setEditNodeType(selectedNode.node_type);
              setEditNodeDialogOpen(true);
            }
          }}
          disabled={!selectedNode}
        >
          Modifier
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteNode}
          disabled={!selectedNode || !selectedNode.parent}
        >
          Supprimer
        </Button>
        {selectedNode?.node_type === 'leaf' && (
          <Button
            variant="outlined"
            onClick={() => {
              loadSessions();
              setSessionDialogOpen(true);
            }}
          >
            Gérer les séances
          </Button>
        )}
      </Box>

      {/* Selected node info */}
      {selectedNode && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: selectedNode.node_type === 'question' ? '#fff3e0' : '#e8f5e9' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Nœud sélectionné ({selectedNode.node_type === 'question' ? 'Question' : 'Feuille'})
          </Typography>
          <Typography variant="body1">{selectedNode.text}</Typography>
          {selectedNode.node_type === 'leaf' && selectedNode.node_sessions && selectedNode.node_sessions.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Séances associées: {selectedNode.node_sessions.map(ns => ns.session_name).join(', ')}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Mind map container */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: 400,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      />

      {/* Add Node Dialog */}
      <Dialog open={addNodeDialogOpen} onClose={() => setAddNodeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un nœud enfant</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Type de nœud</Typography>
            <ToggleButtonGroup
              value={newNodeType}
              exclusive
              onChange={(_, value) => value && setNewNodeType(value)}
              fullWidth
            >
              <ToggleButton value="question">
                <QuestionIcon sx={{ mr: 1 }} /> Question
              </ToggleButton>
              <ToggleButton value="leaf">
                <LeafIcon sx={{ mr: 1 }} /> Feuille
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label={newNodeType === 'question' ? 'Question' : 'Texte de la feuille'}
            fullWidth
            multiline
            rows={2}
            value={newNodeText}
            onChange={(e) => setNewNodeText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNodeDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleAddNode} variant="contained">Ajouter</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Node Dialog */}
      <Dialog open={editNodeDialogOpen} onClose={() => setEditNodeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le nœud</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Type de nœud</Typography>
            <ToggleButtonGroup
              value={editNodeType}
              exclusive
              onChange={(_, value) => value && setEditNodeType(value)}
              fullWidth
            >
              <ToggleButton value="question">
                <QuestionIcon sx={{ mr: 1 }} /> Question
              </ToggleButton>
              <ToggleButton value="leaf">
                <LeafIcon sx={{ mr: 1 }} /> Feuille
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Texte"
            fullWidth
            multiline
            rows={2}
            value={editNodeText}
            onChange={(e) => setEditNodeText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNodeDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleUpdateNode} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* Sessions Dialog */}
      <Dialog open={sessionDialogOpen} onClose={() => setSessionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gérer les séances pour "{selectedNode?.text}"</DialogTitle>
        <DialogContent>
          {loadingSessions ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                Séances associées
              </Typography>
              {selectedNode?.node_sessions && selectedNode.node_sessions.length > 0 ? (
                <List>
                  {selectedNode.node_sessions.map((ns) => (
                    <ListItem key={ns.id}>
                      <ListItemText primary={ns.session_name} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveSession(ns.session)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Aucune séance associée
                </Typography>
              )}

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Ajouter une séance
              </Typography>
              <Autocomplete
                options={availableSessions.filter(
                  s => !selectedNode?.node_sessions?.some(ns => ns.session === s.id)
                )}
                getOptionLabel={(option) => option.name}
                onChange={(_, value) => {
                  if (value) handleAddSession(value);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Rechercher une séance..." />
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SetupMindMap;
